import * as React from 'react';
import styled, { StyledComponent } from 'styled-components';

export const withDefaultProps =
  <P extends Record<string, unknown>, DP extends Partial<P> = Partial<P>>(defaultProps: DP) =>
  (Component: React.ComponentType<P>) => {
    // re-create props definition by creating an intersection type
    type Props = Partial<DP> & Omit<P, keyof DP>;

    // set the default props
    Component.defaultProps = defaultProps;

    // override return type definition by turning off type checker
    // for original props and setting the correct return type
    return Component as React.ComponentType<any> as React.ComponentType<Props>;
  };

export type RenderPropType = React.ElementType | React.ReactChild | React.ComponentClass;

export const renderProp = (RenderProp?: React.ReactNode, defaultProps: Record<string, unknown> = {}) => {
  let result;
  if (RenderProp) {
    if (React.isValidElement(RenderProp)) {
      result = RenderProp;
    } else {
      // I need to explicitely cast as the type guard isValidElement is not working correctly
      const Component = RenderProp as React.ComponentClass;
      result = <Component {...defaultProps} />;
    }
  }
  return result;
};

export const ListUl = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const ListLiStyled = styled.li``;

export const ListLi = withDefaultProps({
  m: 0,
  p: 0,
})(ListLiStyled) as StyledComponent<'li', any, Record<string, unknown>>;

type Item = any;

export interface ListProps {
  /**
   * the items to render in the list
   */
  items?: Item[];
  /**
   * render callback to render a list item
   *
   * @example
   *
   * // pass react component
   * <List renderItem={ProductTile}>
   *
   * // pass function
   * <List renderItem={(props) => (<ProductTile {...props} />)}>
   */
  renderItem: RenderPropType;
  /**
   * A generic accessor for extracting an item from any sort of items array.
   */
  getItem?: (items: Item[], index: number) => Item;
  /**
   * Gets the real index of the item in the items array
   */
  getItemIndex?: (index: number) => number;
  /**
   * Determines how many items are in the items array.
   */
  getItemCount?: (items?: Item[]) => number;
  /**
   * change styling of the container
   */
  className?: string;
  /**
   * getter-function to provide a key, defaults to `item.id`
   */
  keyExtractor?: (item: Item, index?: number) => string;

  /**
   * Additional props to pass from a different context
   */
  additionalProps?: Record<string, unknown>;
}

const ListBase: React.FC<ListProps> = ({
  keyExtractor,
  renderItem,
  items,
  className,
  getItemCount,
  getItemIndex,
  getItem,
  additionalProps,
}) => {
  const itemsCount = getItemCount!(items);
  return (
    <ListUl className={className} data-testid="list-ul">
      {[...Array(itemsCount)].map((_, index: number) => {
        const item = getItem!(items!, index);
        const key = keyExtractor!(item, index);
        return (
          <ListLi key={`${key}-${index}`} data-testid="list-li">
            {renderProp(renderItem, { item, index: getItemIndex ? getItemIndex(index) : index, ...additionalProps })}
          </ListLi>
        );
      })}
    </ListUl>
  );
};

ListBase.defaultProps = {
  keyExtractor: (item: Item) => item.id,
  getItem: (items: Item[], index: number) => items[index],
  getItemCount: (items?: Item[]) => (items ? items.length : 0),
};

export const List = React.memo(ListBase);

(List as any).defaultProps = {
  ...ListBase.defaultProps,
};
